import React, { useState, ReactNode } from 'react';
import { Sidebar, menuItems } from './sidebar';
import { Header } from './header';
import { RouteCacheProvider } from '@/contexts/RouteCacheContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { PageHeaderProvider } from '@/contexts/PageHeaderContext';
import { RouteContainer } from './route-container';

// Re-export the ListPageLayout component for easier imports
export { ListPageLayout } from './list-page-layout';

interface LayoutProps {
	children?: ReactNode;
}

export const Layout: React.FC<LayoutProps> = () => {
	const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile overlay
	const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
		// Load from localStorage, default to expanded (false)
		const saved = localStorage.getItem('sidebar-collapsed');
		return saved ? JSON.parse(saved) : false;
	});

	const toggleSidebar = () => {
		setSidebarOpen(!sidebarOpen);
	};

	const closeSidebar = () => {
		setSidebarOpen(false);
	};

	const toggleSidebarCollapse = () => {
		const newCollapsedState = !sidebarCollapsed;
		setSidebarCollapsed(newCollapsedState);
		localStorage.setItem(
			'sidebar-collapsed',
			JSON.stringify(newCollapsedState)
		);
	};

	return (
		<RouteCacheProvider maxCacheSize={15}>
			<PageHeaderProvider>
				<SidebarProvider>
					<div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
						<Sidebar
							isOpen={sidebarOpen}
							onClose={closeSidebar}
							isCollapsed={sidebarCollapsed}
							onToggleCollapse={toggleSidebarCollapse}
						/>
						{sidebarOpen && (
							<div
								className="kt-mobile-overlay"
								onClick={closeSidebar}
							/>
						)}
						<div
							className={`kt-main h-full flex flex-col ${sidebarCollapsed ? 'kt-main-collapsed' : ''}`}
						>
							<Header
								onMenuClick={toggleSidebar}
								isCollapsed={sidebarCollapsed}
								menuItems={menuItems}
							/>
							<main className="flex-1 overflow-hidden kt-content">
								<RouteContainer />
							</main>
						</div>
					</div>
				</SidebarProvider>
			</PageHeaderProvider>
		</RouteCacheProvider>
	);
};
